import { messages } from '@/shared/constants/messages';
import { formatPhone } from '@/shared/lib/format';
import type { ProjectContact } from '@/shared/types/domain';

interface ResponsibleContactProps {
  /** `undefined` enquanto carrega, `null` quando o projeto não tem responsável. */
  contact: ProjectContact | null | undefined;
}

/**
 * E-mail e telefone do responsável, embaixo do nome, na seção de Identificação
 * (decisão 30).
 *
 * **Sem vínculo vigente, o contato não aparece — e a tela diz o que fazer**, em
 * vez de só relatar o que houve (RNF-U03): "sem vínculo ativo. Fale com a
 * liderança do ministério." Quem saiu da igreja não é mais contatável por aqui;
 * quem só trocou de ministério continua sendo (a vigência olha a liderança
 * inteira, não este ministério).
 *
 * Os dois campos chegam NULOS do próprio banco quando o vínculo acabou — esta
 * tela não é quem filtra dado pessoal, e não deve virar quem filtra.
 */
export function ResponsibleContact({ contact }: ResponsibleContactProps) {
  if (!contact) return null;

  if (!contact.membership_active) {
    return (
      <span className="mt-0.5 block text-xs font-normal text-muted-foreground">
        {messages.projects.contactInactive}
      </span>
    );
  }

  return (
    <span className="mt-0.5 flex flex-wrap items-center gap-x-2 gap-y-0.5 text-xs font-normal">
      {contact.email ? (
        <a
          href={`mailto:${contact.email}`}
          className="rounded text-primary hover:underline focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-ring"
        >
          {contact.email}
        </a>
      ) : null}
      {contact.email && contact.phone ? (
        <span aria-hidden="true" className="text-border">
          ·
        </span>
      ) : null}
      {contact.phone ? (
        <a
          href={`tel:${contact.phone}`}
          className="rounded text-primary hover:underline focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-ring"
        >
          {formatPhone(contact.phone)}
        </a>
      ) : null}
    </span>
  );
}
